// "Nuovo" in lista (15/09/2026, ispirato a Gmail): una conversazione che
// ENTRA in lista mentre l'operatore la sta guardando mostra per qualche
// secondo una pillola accanto al nome e uno sfondo tenue, poi entrambi
// svaniscono. Vale per ogni canale (email, WhatsApp, chatbot, voice).
//
// Il segnale e' l'inserimento realtime (CONVERSATION_PLACEMENT_SOURCE.REALTIME:
// conversation.created, oppure conversation.updated / snapshot da
// message.created che porta una conversazione non ancora in lista). Le righe
// che arrivano da fetch di pagina, ripristino cache, paginazione o reload NON
// sono "nuove" per chi guarda, e una riga gia' in lista che riceve un
// messaggio si aggiorna ma non e' un nuovo thread: Gmail marca i thread nuovi,
// non quelli aggiornati.
//
// Stato nello store conversations:
//   freshArrivals[conversationId] = epoch ms dell'inserimento realtime
// La mappa e' separata dalla riga (i campi __transient vengono spogliati
// in UPDATE_CONVERSATION e le fusioni di fetch/cache le sovrascriverebbero).
// Le funzioni qui sono pure: ricevono la mappa e ne restituiscono una nuova,
// oppure null quando non c'e' nulla da cambiare.

export const CONVERSATION_FRESH_ARRIVAL_DURATION_MS = 6000;

const resolveFreshArrivalKey = (conversationId = null) =>
  conversationId === null ||
  conversationId === undefined ||
  conversationId === ''
    ? null
    : String(conversationId);

const resolveNowMs = (nowMs = () => Date.now()) => {
  const value = typeof nowMs === 'function' ? nowMs() : nowMs;
  return Number.isFinite(Number(value)) ? Number(value) : Date.now();
};

const resolveDurationMs = (
  durationMs = CONVERSATION_FRESH_ARRIVAL_DURATION_MS
) =>
  Number.isFinite(Number(durationMs)) && Number(durationMs) > 0
    ? Number(durationMs)
    : CONVERSATION_FRESH_ARRIVAL_DURATION_MS;

export const getConversationFreshArrivalAt = ({
  conversationId = null,
  freshArrivals = {},
} = {}) => {
  const key = resolveFreshArrivalKey(conversationId);
  if (key === null || !freshArrivals) return null;

  const arrivedAt = Number(freshArrivals[key]);
  return Number.isFinite(arrivedAt) && arrivedAt > 0 ? arrivedAt : null;
};

// Millisecondi che restano alla finestra "Nuovo": 0 se assente o scaduta.
export const resolveConversationFreshArrivalRemainingMs = ({
  conversationId = null,
  durationMs = CONVERSATION_FRESH_ARRIVAL_DURATION_MS,
  freshArrivals = {},
  nowMs = () => Date.now(),
} = {}) => {
  const arrivedAt = getConversationFreshArrivalAt({
    conversationId,
    freshArrivals,
  });
  if (arrivedAt === null) return 0;

  const remainingMs =
    arrivedAt + resolveDurationMs(durationMs) - resolveNowMs(nowMs);
  return remainingMs > 0 ? remainingMs : 0;
};

export const isConversationFreshArrival = (options = {}) =>
  resolveConversationFreshArrivalRemainingMs(options) > 0;

// Spoglia le voci scadute: ritorna la mappa nuova o null se nulla e' scaduto.
export const pruneExpiredConversationFreshArrivals = ({
  durationMs = CONVERSATION_FRESH_ARRIVAL_DURATION_MS,
  freshArrivals = {},
  nowMs = () => Date.now(),
} = {}) => {
  if (!freshArrivals) return null;

  const now = resolveNowMs(nowMs);
  const duration = resolveDurationMs(durationMs);
  const expiredKeys = Object.keys(freshArrivals).filter((key) => {
    const arrivedAt = Number(freshArrivals[key]);
    return !(Number.isFinite(arrivedAt) && arrivedAt + duration > now);
  });
  if (!expiredKeys.length) return null;

  const nextFreshArrivals = { ...freshArrivals };
  expiredKeys.forEach((key) => {
    delete nextFreshArrivals[key];
  });
  return nextFreshArrivals;
};

// Timbra l'ingresso realtime e, nello stesso passaggio, spoglia le voci
// scadute cosi' la mappa non cresce oltre gli arrivi degli ultimi secondi.
export const markConversationFreshArrival = ({
  conversationId = null,
  durationMs = CONVERSATION_FRESH_ARRIVAL_DURATION_MS,
  freshArrivals = {},
  nowMs = () => Date.now(),
} = {}) => {
  const key = resolveFreshArrivalKey(conversationId);
  if (key === null) return null;

  const now = resolveNowMs(nowMs);
  const pruned =
    pruneExpiredConversationFreshArrivals({
      durationMs,
      freshArrivals,
      nowMs: now,
    }) ||
    freshArrivals ||
    {};
  return { ...pruned, [key]: now };
};

export const clearConversationFreshArrival = ({
  conversationId = null,
  freshArrivals = {},
} = {}) => {
  const key = resolveFreshArrivalKey(conversationId);
  if (
    key === null ||
    !freshArrivals ||
    !Object.prototype.hasOwnProperty.call(freshArrivals, key)
  ) {
    return null;
  }

  const nextFreshArrivals = { ...freshArrivals };
  delete nextFreshArrivals[key];
  return nextFreshArrivals;
};
